import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createHospital, lookupPincodeDetail } from "../services/hospitalService";

const getCurrentRole = () => {
  const authRole = localStorage.getItem("authRole");
  const roleMapping = {
    wallet_superadmin: "SuperAdmin",
    wallet_finance: "Finance",
    wallet_ops: "Ops",
    wallet_support: "Support",
  };
  return roleMapping[authRole] || null;
};

const HOSPITAL_TYPES = [
  { value: "multi_specialty", label: "Multi Specialty" },
  { value: "single_specialty", label: "Single Specialty" },
  { value: "daycare", label: "Day Care" },
  { value: "diagnostic", label: "Diagnostic" },
];
const OT_TYPE_OPTIONS = ["General", "Cardiac", "Neuro", "Orthopedic"];
const ACCREDITATION_OPTIONS = ["NABH", "JCI"];
const SPECIALTIES_OFFERED = [
  { label: "Cardiology & Cardiothoracic Surgery", value: "cardiology_cardiothoracic_surgery" },
  { label: "Neurology & Neurosurgery", value: "neurology_neurosurgery" },
  { label: "Orthopedics & Joint Replacement", value: "orthopedics_joint_replacement" },
  { label: "Gastroenterology & Hepatology", value: "gastroenterology_hepatology" },
  { label: "Oncology & Hemato-Oncology", value: "oncology_hemato_oncology" },
  { label: "Gynecology & Obstetrics", value: "gynecology_obstetrics" },
  { label: "Pediatrics & Neonatology", value: "pediatrics_neonatology" },
  { label: "Nephrology & Urology", value: "nephrology_urology" },
  { label: "Pulmonology & Critical Care", value: "pulmonology_critical_care" },
  { label: "ENT & Head-Neck Surgery", value: "ent_head_neck_surgery" },
  { label: "Ophthalmology", value: "ophthalmology" },
  { label: "Dermatology & Cosmetology", value: "dermatology_cosmetology" },
  { label: "Endocrinology & Diabetology", value: "endocrinology_diabetology" },
  { label: "General Surgery & Laparoscopy", value: "general_surgery_laparoscopy" },
  { label: "Plastic & Reconstructive Surgery", value: "plastic_reconstructive_surgery" },
  { label: "Mental Health & Psychiatry", value: "mental_health_psychiatry" },
  { label: "Emergency Medicine", value: "emergency_medicine" },
  { label: "General Medicine & Family Care", value: "general_medicine_family_care" },
];

const DIAGNOSTIC_SERVICES_OPTIONS = [
  { label: "X-Ray", value: "x_ray" },
  { label: "CT Scan", value: "ct_scan" },
  { label: "MRI", value: "mri" },
  { label: "PET Scan", value: "pet_scan" },
  { label: "Echocardiography", value: "echocardiography" },
  { label: "Ultrasound", value: "ultrasound" },
  { label: "Pathology Labs", value: "pathology_labs" },
];

const AMENITY_OPTIONS = [
  { label: "ATM", value: "atm" },
  { label: "Drinking Water", value: "drinking_water" },
  { label: "AC", value: "ac" },
  { label: "Food Court", value: "food_court" },
  { label: "Washrooms", value: "washrooms" },
  { label: "Lounge", value: "lounge" },
  { label: "Prayer Rooms", value: "prayer_rooms" },
  { label: "Parking", value: "parking" },
  { label: "WiFi", value: "wifi" },
  { label: "Cafeteria", value: "cafeteria" },
  { label: "Ambulance", value: "ambulance" },
];

// ─── Shared sub-components defined OUTSIDE to prevent remount on every render ───

const inputCls = (field, validationErrors) =>
  `w-full px-3 py-2 text-sm border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    validationErrors[field]
      ? "border-red-400 bg-red-50"
      : "border-gray-300 bg-white hover:border-gray-400"
  }`;

const SectionCard = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
      <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const ChipToggle = ({ selected, onClick, children }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
      selected
        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
        : "bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600"
    }`}
  >
    {children}
  </button>
);

const ToggleSwitch = ({ name, checked, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer group">
    <div className="relative flex-shrink-0">
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        className="sr-only"
        tabIndex={-1}
      />
      <div className={`w-10 h-5 rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-gray-300"}`} />
      <div
        className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </div>
    <span className="text-sm text-gray-700 group-hover:text-gray-900">{label}</span>
  </label>
);

// ────────────────────────────────────────────────────────────────────────────────

const AddHospital = () => {
  const navigate = useNavigate();
  const currentRole = getCurrentRole();
  const canCreate = currentRole === "Ops" || currentRole === "SuperAdmin";

  const [submitting, setSubmitting] = useState(false);
  const [pinLookupLoading, setPinLookupLoading] = useState(false);
  const [localities, setLocalities] = useState([]);
  // const [latLngLookupLoading, setLatLngLookupLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    pincode: "",
    address_line: "",
    city: "",
    state: "",
    sublocality: "",
    hospital_type: "",
    specialty: "",
    accreditations: [],
    no_of_beds: "",
    no_of_doctors: "",
    no_of_ot: "",
    ot_types: [],
    amenities: [],
    diagnostic_services: [],
    specialities_offered: [],
    no_of_icus: "",
    no_of_wards: "",
    has_pharmacy: false,
    has_radiology: false,
    has_emergency: false,
    has_blood_bank: false,
    // latitude: "",
    // longitude: "",
    bus_stand_in_kms: "",
    railway_in_kms: "",
    airport_in_kms: "",
  });

  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const onMultiSelectToggle = (name, value) => {
    setFormData((prev) => {
      const current = prev[name] || [];
      const exists = current.includes(value);
      return {
        ...prev,
        [name]: exists ? current.filter((v) => v !== value) : [...current, value],
      };
    });
    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const cleanCityState = (text) => {
    return text.replace(/[0-9]/g, "").trim();
  };

  const lookupPincode = async (pincode) => {
    if (!/^\d{6}$/.test(pincode)) return;
    setPinLookupLoading(true);
    try {
      const msg = await lookupPincodeDetail(pincode);
      const locations = msg?.locations || [];
      const first = locations[0];
      if (!first) {
        setLocalities([]);
        return;
      }

      // `district` is the city; `city` is the locality (post-office) name.
      const city = cleanCityState(first.district);
      const state = cleanCityState(first.state);
      // A pincode maps to multiple localities — expose them all so the user
      // can pick the correct one from a dropdown.
      const names = [...new Set(locations.map((l) => l.city).filter(Boolean))];
      setLocalities(names);
      const sublocality = names[0] || "";

      setFormData((prev) => ({ ...prev, city, state, sublocality }));

      // const query = encodeURIComponent(`${pincode}, ${city}, ${state}, India`);
      // const geoRes = await fetch(
      //   `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1`
      // );
      // const geoData = await geoRes.json();
      // if (geoData?.length > 0) {
      //   setFormData((prev) => ({
      //     ...prev,
      //     latitude: geoData[0].lat,
      //     longitude: geoData[0].lon,
      //   }));
      // }
    } catch (err) {
      console.error("Pincode lookup failed", err);
    } finally {
      setPinLookupLoading(false);
    }
  };

  // const lookupLatLng = async (lat, lng) => {
  //   const latNum = parseFloat(lat);
  //   const lngNum = parseFloat(lng);
  //   if (isNaN(latNum) || isNaN(lngNum)) return;
  //   if (latNum < -90 || latNum > 90 || lngNum < -180 || lngNum > 180) return;

  //   setLatLngLookupLoading(true);
  //   try {
  //     const res = await fetch(
  //       `https://nominatim.openstreetmap.org/reverse?lat=${latNum}&lon=${lngNum}&format=json`
  //     );
  //     const data = await res.json();
  //     const addr = data?.address;
  //     if (!addr) return;

  //     const pincode = addr.postcode?.replace(/\s/g, "") || "";
  //     const city = addr.city || addr.town || addr.county || addr.district || "";
  //     const state = addr.state || "";
  //     const locality = addr.suburb || addr.neighbourhood || addr.village || addr.hamlet || "";

  //     setFormData((prev) => ({
  //       ...prev,
  //       ...(pincode && { pincode }),
  //       ...(city && { city }),
  //       ...(state && { state }),
  //       ...(locality && { locality }),
  //     }));
  //     setValidationErrors((prev) => ({
  //       ...prev,
  //       ...(pincode && { pincode: "" }),
  //       ...(city && { city: "" }),
  //       ...(state && { state: "" }),
  //       ...(locality && { locality: "" }),
  //     }));
  //   } catch (err) {
  //     console.error("Lat/Lng reverse lookup failed", err);
  //   } finally {
  //     setLatLngLookupLoading(false);
  //   }
  // };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim() || formData.name.trim().length > 100) {
      errors.name = "Hospital name is required and must be ≤ 100 characters.";
    }
    if (!formData.address_line.trim() || formData.address_line.trim().length > 200) {
      errors.address_line = "Full address is required and must be ≤ 200 characters.";
    }
    if (!formData.sublocality.trim() || formData.sublocality.trim().length > 50) {
      errors.sublocality = "Locality is required and must be ≤ 50 characters.";
    }
    if (!/^\d{6}$/.test(formData.pincode)) errors.pincode = "PIN code must be 6 digits.";
    if (!formData.state.trim()) {
      errors.state = "State is required.";
    } else if (!/^[a-zA-Z\s\-&.,']+$/.test(formData.state.trim())) {
      errors.state = "State name can only contain letters, spaces, and hyphens.";
    }
    if (!formData.city.trim()) {
      errors.city = "City is required.";
    } else if (!/^[a-zA-Z\s\-&.,']+$/.test(formData.city.trim())) {
      errors.city = "City name can only contain letters, spaces, and hyphens.";
    }
    if (formData.hospital_type === "single_specialty" && !formData.specialty.trim()) {
      errors.specialty = "Specialty name is required for single specialty hospitals.";
    }

    const numberRangeCheck = (field, min, max, label) => {
      const val = formData[field];
      const num = Number(val);
      if (val === "" || Number.isNaN(num) || num < min || num > max) {
        errors[field] = `${label} must be between ${min} and ${max}.`;
      }
    };

    numberRangeCheck("no_of_beds", 1, 9999, "Number of beds");
    numberRangeCheck("no_of_doctors", 1, 999, "Number of doctors");

    // Optional numeric fields — only validate if filled
    const optionalRangeCheck = (field, min, max, label) => {
      const val = formData[field];
      if (val === "") return;
      const num = Number(val);
      if (Number.isNaN(num) || num < min || num > max) {
        errors[field] = `${label} must be between ${min} and ${max}.`;
      }
    };

    optionalRangeCheck("no_of_ot", 0, 99, "Number of operation theatres");
    optionalRangeCheck("no_of_icus", 0, 99, "Number of ICUs");
    optionalRangeCheck("no_of_wards", 0, 999, "Number of wards");

    ["bus_stand_in_kms", "railway_in_kms", "airport_in_kms"].forEach((field) => {
      const val = formData[field];
      if (val === "") return;
      const num = Number(val);
      if (Number.isNaN(num) || num < 0) {
        errors[field] = "Distance must be a valid non-negative number.";
      }
    });

    // if (!formData.latitude) {
    //   errors.latitude = "Latitude is required.";
    // }
    // if (!formData.longitude) {
    //   errors.longitude = "Longitude is required.";
    // }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setMessage({ type: "error", text: "Please fix the highlighted errors before submitting." });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const payload = {
        name: formData.name.trim(),
        hospital_type: formData.hospital_type,
        other_hospital_type: null,

        address_line: formData.address_line.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        sublocality: formData.sublocality.trim(),
        pincode: formData.pincode,
        // latitude: formData.latitude ? String(formData.latitude) : null,
        // longitude: formData.longitude ? String(formData.longitude) : null,

        facility_type: formData.hospital_type,
        specialty: formData.hospital_type === "single_specialty" ? formData.specialty.trim() : "",
        accreditations: formData.accreditations,

        no_of_bed: formData.no_of_beds !== "" ? Number(formData.no_of_beds) : null,
        no_of_doctor: formData.no_of_doctors !== "" ? Number(formData.no_of_doctors) : null,
        no_of_ot: formData.no_of_ot !== "" ? Number(formData.no_of_ot) : null,
        ot_types: formData.ot_types,
        no_of_icu: formData.no_of_icus !== "" ? Number(formData.no_of_icus) : null,
        no_of_ward: formData.no_of_wards !== "" ? Number(formData.no_of_wards) : null,

        has_pharmacy: formData.has_pharmacy,
        has_radiology: formData.has_radiology,
        has_emergency: formData.has_emergency,
        has_blood_bank: formData.has_blood_bank,

        bus_stand_in_km: formData.bus_stand_in_kms !== "" ? Number(formData.bus_stand_in_kms) : 0,
        railway_in_km: formData.railway_in_kms !== "" ? Number(formData.railway_in_kms) : 0,
        airport_in_km: formData.airport_in_kms !== "" ? Number(formData.airport_in_kms) : 0,

        amenities: formData.amenities,
        diagnostic_services: formData.diagnostic_services,
        specialties_offered: formData.specialities_offered,
      };

      await createHospital(currentRole, payload);
      setMessage({ type: "success", text: "Hospital created successfully." });
      setTimeout(() => navigate("/subscriber-wallet/hospitals"), 1000);
    } catch (error) {
      console.error("API ERROR:", error.response?.data || error);
      setMessage({
        type: "error",
        text:
          error.response?.data?.error ||
          JSON.stringify(error.response?.data) ||
          "Failed to create hospital.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!canCreate) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-500 text-sm">You do not have permission to add hospitals.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4 sm:px-8">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate("/subscriber-wallet/hospitals")}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Hospitals
          </button>
          <div className="text-right">
            <h1 className="text-xl font-bold text-gray-900">Add New Hospital</h1>
            <p className="text-xs text-gray-500 mt-0.5">Fill in the details to onboard a hospital</p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-6" noValidate>

          {/* Basic Information */}
          <SectionCard title="Basic Information" subtitle="Name and classification of the hospital">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                  Hospital Name <span className="text-red-500 normal-case font-normal">*</span>
                </label>
                <input
                  name="name"
                  maxLength={100}
                  value={formData.name}
                  onChange={onInputChange}
                  placeholder="e.g. Apollo Hospitals, Mumbai"
                  className={inputCls("name", validationErrors)}
                />
                {validationErrors.name && <p className="text-xs text-red-600 mt-1">{validationErrors.name}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                  Hospital Type
                </label>
                <select
                  name="hospital_type"
                  value={formData.hospital_type}
                  onChange={onInputChange}
                  className={inputCls("hospital_type", validationErrors)}
                >
                  <option value="">Select type...</option>
                  {HOSPITAL_TYPES.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {formData.hospital_type === "single_specialty" && (
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    Specialty Name <span className="text-red-500 normal-case font-normal">*</span>
                  </label>
                  <input
                    name="specialty"
                    value={formData.specialty}
                    onChange={onInputChange}
                    placeholder="e.g. Cardiology"
                    className={inputCls("specialty", validationErrors)}
                  />
                  {validationErrors.specialty && <p className="text-xs text-red-600 mt-1">{validationErrors.specialty}</p>}
                </div>
              )}

              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                  Accreditations
                </label>
                <div className="flex gap-2 flex-wrap">
                  {ACCREDITATION_OPTIONS.map((acc) => (
                    <ChipToggle
                      key={acc}
                      selected={formData.accreditations.includes(acc)}
                      onClick={() => onMultiSelectToggle("accreditations", acc)}
                    >
                      {acc}
                    </ChipToggle>
                  ))}
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Location */}
          <SectionCard title="Location" subtitle="Address and geographic coordinates">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                  Full Address <span className="text-red-500 normal-case font-normal">*</span>
                </label>
                <input
                  name="address_line"
                  maxLength={200}
                  value={formData.address_line}
                  onChange={onInputChange}
                  placeholder="Building, street, area..."
                  className={inputCls("address_line", validationErrors)}
                />
                {validationErrors.address_line && <p className="text-xs text-red-600 mt-1">{validationErrors.address_line}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                  PIN Code <span className="text-red-500 normal-case font-normal">*</span>
                </label>
                <div className="relative">
                  <input
                    name="pincode"
                    value={formData.pincode}
                    placeholder="6-digit PIN"
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d{0,6}$/.test(val)) {
                        onInputChange(e);
                        if (val.length === 6) lookupPincode(val);
                      }
                    }}
                    className={inputCls("pincode", validationErrors)}
                  />
                  {pinLookupLoading && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-500 font-medium">
                      Looking up...
                    </span>
                  )}
                  {!pinLookupLoading && formData.pincode.length === 6 && formData.city && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-green-600 font-medium">
                      Auto-filled
                    </span>
                  )}
                </div>
                {validationErrors.pincode && <p className="text-xs text-red-600 mt-1">{validationErrors.pincode}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                  Locality <span className="text-red-500 normal-case font-normal">*</span>
                </label>
                <select
                  name="sublocality"
                  value={formData.sublocality}
                  onChange={onInputChange}
                  disabled={pinLookupLoading || localities.length === 0}
                  className={`${inputCls("sublocality", validationErrors)} disabled:bg-gray-50 disabled:text-gray-400`}
                >
                  <option value="">
                    {pinLookupLoading
                      ? "Loading localities..."
                      : localities.length === 0
                        ? "Enter a PIN code first"
                        : "Select locality"}
                  </option>
                  {/* Preserve a previously-chosen locality even if it isn't in the fetched list */}
                  {formData.sublocality && !localities.includes(formData.sublocality) && (
                    <option value={formData.sublocality}>{formData.sublocality}</option>
                  )}
                  {localities.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                {validationErrors.sublocality && <p className="text-xs text-red-600 mt-1">{validationErrors.sublocality}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                  City <span className="text-red-500 normal-case font-normal">*</span>
                </label>
                <input
                  name="city"
                  value={formData.city}
                  onChange={onInputChange}
                  placeholder="City"
                  className={inputCls("city", validationErrors)}
                />
                {validationErrors.city && <p className="text-xs text-red-600 mt-1">{validationErrors.city}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                  State <span className="text-red-500 normal-case font-normal">*</span>
                </label>
                <input
                  name="state"
                  value={formData.state}
                  onChange={onInputChange}
                  placeholder="State"
                  className={inputCls("state", validationErrors)}
                />
                {validationErrors.state && <p className="text-xs text-red-600 mt-1">{validationErrors.state}</p>}
              </div>

              {/* <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                  Latitude <span className="text-red-500 normal-case font-normal">*</span>
                </label>
                <div className="relative">
                  <input
                    name="latitude"
                    value={formData.latitude}
                    onChange={(e) => {
                      onInputChange(e);
                      const lat = e.target.value;
                      const lng = formData.longitude;
                      if (lat && lng) lookupLatLng(lat, lng);
                    }}
                    placeholder="e.g. 19.0760"
                    className={inputCls("latitude", validationErrors)}
                  />
                  {latLngLookupLoading && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-blue-500 font-medium">
                      Looking up...
                    </span>
                  )}
                  {!latLngLookupLoading && formData.latitude && formData.longitude && formData.pincode && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-green-600 font-medium">
                      Auto-filled
                    </span>
                  )}
                </div>
                {validationErrors.latitude && <p className="text-xs text-red-600 mt-1">{validationErrors.latitude}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                  Longitude <span className="text-red-500 normal-case font-normal">*</span>
                </label>
                <div className="relative">
                  <input
                    name="longitude"
                    value={formData.longitude}
                    onChange={(e) => {
                      onInputChange(e);
                      const lng = e.target.value;
                      const lat = formData.latitude;
                      if (lat && lng) lookupLatLng(lat, lng);
                    }}
                    placeholder="e.g. 72.8777"
                    className={inputCls("longitude", validationErrors)}
                  />
                </div>
                {validationErrors.longitude && <p className="text-xs text-red-600 mt-1">{validationErrors.longitude}</p>}
              </div> */}
            </div>
          </SectionCard>

          {/* Capacity & Infrastructure */}
          <SectionCard title="Capacity & Infrastructure" subtitle="Operational capacity details">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
              {[
                { name: "no_of_beds", label: "No. of Beds", required: true, hint: "1–9999" },
                { name: "no_of_doctors", label: "No. of Doctors", required: true, hint: "1–999" },
                { name: "no_of_ot", label: "Operation Theatres", required: false, hint: "0–99" },
                { name: "no_of_icus", label: "ICUs", required: false, hint: "0–99" },
                { name: "no_of_wards", label: "Wards", required: false, hint: "0–999" },
              ].map(({ name, label, required, hint }) => (
                <div key={name}>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    {label}{" "}
                    {required && <span className="text-red-500 normal-case font-normal">*</span>}
                    {hint && <span className="text-gray-400 font-normal normal-case ml-1">({hint})</span>}
                  </label>
                  <input
                    type="number"
                    min={0}
                    name={name}
                    value={formData[name]}
                    onChange={onInputChange}
                    className={inputCls(name, validationErrors)}
                  />
                  {validationErrors[name] && <p className="text-xs text-red-600 mt-1">{validationErrors[name]}</p>}
                </div>
              ))}
            </div>

            <div className="mt-6">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">OT Types</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {OT_TYPE_OPTIONS.map((ot) => (
                  <ToggleSwitch
                    key={ot}
                    name={ot}
                    checked={formData.ot_types.includes(ot)}
                    onChange={() => onMultiSelectToggle("ot_types", ot)}
                    label={ot}
                  />
                ))}
              </div>
            </div>
          </SectionCard>

          {/* Services & Facilities */}
          <SectionCard title="Services & Facilities" subtitle="Available services and on-site amenities">
            <div className="space-y-6">
              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Core Services</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {[
                    ["has_pharmacy", "Pharmacy"],
                    ["has_radiology", "Radiology"],
                    ["has_emergency", "24x7 Emergency"],
                    ["has_blood_bank", "Blood Bank"],
                  ].map(([name, label]) => (
                    <ToggleSwitch
                      key={name}
                      name={name}
                      checked={Boolean(formData[name])}
                      onChange={onInputChange}
                      label={label}
                    />
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Amenities</p>
                <div className="flex gap-2 flex-wrap">
                  {AMENITY_OPTIONS.map(({ label, value }) => (
                    <ChipToggle
                      key={value}
                      selected={formData.amenities.includes(value)}
                      onClick={() => onMultiSelectToggle("amenities", value)}
                    >
                      {label}
                    </ChipToggle>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Diagnostic Services</p>
                <div className="flex gap-2 flex-wrap">
                  {DIAGNOSTIC_SERVICES_OPTIONS.map(({ label, value }) => (
                    <ChipToggle
                      key={value}
                      selected={formData.diagnostic_services.includes(value)}
                      onClick={() => onMultiSelectToggle("diagnostic_services", value)}
                    >
                      {label}
                    </ChipToggle>
                  ))}
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Specialties Offered */}
          <SectionCard title="Specialties Offered" subtitle="Medical specialties available at this hospital">
            <div className="flex gap-2 flex-wrap">
              {SPECIALTIES_OFFERED.map(({ label, value }) => (
                <ChipToggle
                  key={value}
                  selected={formData.specialities_offered.includes(value)}
                  onClick={() => onMultiSelectToggle("specialities_offered", value)}
                >
                  {label}
                </ChipToggle>
              ))}
            </div>
          </SectionCard>

          {/* Distance from Transit */}
          <SectionCard title="Nearby Transit" subtitle="Approximate distances from key transport hubs (in km)">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                ["bus_stand_in_kms", "Bus Station"],
                ["railway_in_kms", "Railway Station"],
                ["airport_in_kms", "Airport"],
              ].map(([name, label]) => (
                <div key={name}>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                    {label}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      name={name}
                      value={formData[name]}
                      onChange={onInputChange}
                      placeholder="0.0"
                      className={`${inputCls(name, validationErrors)} pr-10`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium pointer-events-none">
                      km
                    </span>
                  </div>
                  {validationErrors[name] && <p className="text-xs text-red-600 mt-1">{validationErrors[name]}</p>}
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Message */}
          {message && (
            <div
              className={`flex items-start gap-3 px-4 py-3 rounded-lg border text-sm ${
                message.type === "success"
                  ? "bg-green-50 border-green-200 text-green-800"
                  : "bg-red-50 border-red-200 text-red-800"
              }`}
            >
              <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {message.type === "success" ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                )}
              </svg>
              {message.text}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pb-6">
            <button
              type="button"
              onClick={() => navigate("/subscriber-wallet/hospitals")}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-lg shadow-sm transition-all ${
                submitting ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700 active:scale-95"
              }`}
            >
              {submitting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create Hospital
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddHospital;
